import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import React from "react";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";

import "./style.scss";
import { Badges, H4, MUIIcons, P, ToolTip } from "../../AbstractElements";
import DividerWithoutPadding from "../../CommonElements/DividerWithoutPadding";
import { Archive, EditDetails, UnArchive } from "../../utils/Constant";
import { sanitizeString } from "../../utils/helper/helper";
import { hasPermissionToComponent } from "../../utils/helper/permission";

type LearningPlanCardProps = {
  id: string;
  planType: string;
  title: string;
  creator: string[];
  totalDuration: string;
  learnersCount: number;
  groupsCount: number;
  thumbnailUrl: string;
  isArchived?: boolean;
  onEditDetails?: () => void;
  onArchiveToggle?: () => void;
  courses: number;
  assessments: number;
  showDrop?: boolean;
  learningPlanDate?: string;
  overdue?: boolean;
  userManagement?: boolean;
  isHideDropdown?: boolean;
  score?: number;
  userId?: any;
  userGroupPlan?: boolean;
};

const LearningPlanCard: React.FC<LearningPlanCardProps> = ({
  id,
  planType,
  title,
  creator,
  totalDuration,
  learnersCount,
  groupsCount,
  thumbnailUrl,
  isArchived,
  onEditDetails,
  onArchiveToggle,
  courses,
  assessments,
  showDrop = true,
  learningPlanDate,
  overdue,
  userManagement = false,
  isHideDropdown,
  score = 0,
  userId,
  userGroupPlan = false,
}) => {
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const navigate = useNavigate();

  const handleNavigate = () => {
    if (userManagement) {
      if (hasPermissionToComponent("USER_MANAGEMENT.TM_LEARNING_PLAN")) {
        return navigate(
          `${process.env.PUBLIC_URL}/user-management/learning-plan-detail?id=${id}&userId=${userId}`
        );
      } else {
        return navigate(
          `${process.env.PUBLIC_URL}/user-management/learning-plan-detail?id=${id}`
        );
      }
    }
    return navigate(
      `${process.env.PUBLIC_URL}/master-data/learning-plan-detail?id=${id}`
    );
  };

  return (
    <Card className="learning-plan-card" onClick={handleNavigate}>
      <div className="card-image-wrapper">
        <img
          src={thumbnailUrl || "/assets/images/course-card-default.svg"}
          alt={title}
          className="course-card__image"
          onError={e => {
            const target = e.target as HTMLImageElement;
            target.onerror = null;
            target.src = "/assets/images/course-card-default.svg";
          }}
        />

        <Badges
          className="d-flex gap-2 module-info align-items-center"
          badgeTextColor
          status="re_attempt"
        >
          <div className="stat-item">
            <MUIIcons size={20} className="icon mb-1" iconName="BookOutlined" />
            {courses}
          </div>
          <div className="divider me-0" />
          <div className="stat-item">
            <MUIIcons
              size={20}
              className="icon mb-1"
              iconName="DescriptionOutlined"
            />
            {assessments}
          </div>
        </Badges>
      </div>

      <CardBody>
        <div>
          <div className="d-flex justify-content-between mt-2">
            <div className={userManagement && "w-75"}>
              <div className="plan-type text-uppercase">{planType}</div>
              <div className="creator mt-2">
                <span id={sanitizeString(`tooltip-learning-plan-${id}`)}>
                  <div className="text-ellips-parent">
                    <H4
                      className="header-text text-ellips pointer"
                      data-fulltext={title}
                    >
                      {title}
                    </H4>
                  </div>
                </span>
                <ToolTip
                  placement="top"
                  target={sanitizeString(`tooltip-learning-plan-${id}`)}
                >
                  {title}
                </ToolTip>
              </div>
            </div>
            {!isHideDropdown && !userGroupPlan && showDrop && (
              <Dropdown
                isOpen={dropdownOpen}
                toggle={() => setDropdownOpen(prev => !prev)}
                onClick={e => e.stopPropagation()}
              >
                <DropdownToggle tag="span" className="dropdown-toggle-icon">
                  <MoreVertIcon />
                </DropdownToggle>
                <DropdownMenu end>
                  {!isArchived ? (
                    <>
                      <DropdownItem onClick={onEditDetails}>
                        {EditDetails}
                      </DropdownItem>
                      <DropdownItem onClick={onArchiveToggle}>
                        {Archive}
                      </DropdownItem>
                    </>
                  ) : (
                    <DropdownItem onClick={onArchiveToggle}>
                      {UnArchive}
                    </DropdownItem>
                  )}
                </DropdownMenu>
              </Dropdown>
            )}
            {!showDrop && (
              <div className="new-progress-text" style={{ width: "55px" }}>
                <CircularProgressbar
                  value={score}
                  text={`${score}%`}
                  circleRatio={0.75}
                  strokeWidth={10}
                  styles={buildStyles({
                    rotation: 1 / 2 + 1 / 8,
                    strokeLinecap: "butt",
                    pathColor: "rgba(0, 0, 0, 0.8)",
                    textColor: "#000",
                    trailColor: "#d6d6d6",
                    backgroundColor: "#000",
                    textSize: "22px",
                  })}
                />
              </div>
            )}
          </div>
          <DividerWithoutPadding />
          {showDrop && (
            <div className="creator w-100 mt-2">
              <MUIIcons iconName="PersonOutlined" size={20} className="me-1" />
              <span id={sanitizeString(`tooltip-learning-plan-name-${id}`)}>
                <div className="text-ellips-parent">
                  <P
                    className="header-text text-ellips pointer"
                    data-fulltext={creator}
                  >
                    {creator[0] || "-"}
                  </P>
                </div>
              </span>
              <span id={sanitizeString(`tooltip-learning-plan-name-${id}`)}>
                <div className="text-ellips-parent">
                  <P
                    className="header-text text-ellips pointer"
                    data-fulltext={creator}
                  >
                    {creator?.length > 1 && (
                      <div className="ms-2">{" +" + (creator?.length - 1)}</div>
                    )}
                  </P>
                </div>
              </span>
              <ToolTip
                placement="top"
                target={sanitizeString(`tooltip-learning-plan-name-${id}`)}
              >
                {creator?.join(", ")}
              </ToolTip>
            </div>
          )}
          {!showDrop && (
            <div className="creator w-100 mt-2">
              <MUIIcons
                iconName="CalendarTodayOutlined"
                size={18}
                className="me-1"
              />
              <span className={`pointer ${overdue && "primary-text-color"} `}>
                {learningPlanDate}
              </span>
            </div>
          )}
          <DividerWithoutPadding />
        </div>

        <div className="card-footer-info d-flex justify-content-between mt-2 mb-2">
          <div className="info-item d-flex align-items-center gap-1 mt-1">
            <AccessTimeIcon fontSize="small" />
            <span>{totalDuration}</span>
          </div>

          {showDrop && !userGroupPlan && (
            <Badges
              className="d-flex gap-2 module-info-2 align-items-center"
              badgeTextColor
              status="re_attempt"
            >
              <div className="stat-item">
                <MUIIcons
                  size={20}
                  className="icon mb-1"
                  iconName="PersonOutlined"
                />
                {learnersCount}
              </div>
              <div className="divider me-0" />
              <div className="stat-item">
                <MUIIcons
                  size={20}
                  className="icon mb-1"
                  iconName="GroupsOutlined"
                />
                {groupsCount?.toString().padStart(2, "0")}
              </div>
            </Badges>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default LearningPlanCard;
